import React from 'react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Container } from '../../components/ui/Container';
import { Section } from '../../components/ui/Section';
import { Shield, FileText, CheckCircle, Users, Scale, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
export function Governance() {
  return <Layout data-id="element-953">
      <Section spacing="lg" data-id="element-954">
        <Container size="lg" data-id="element-955">
          {/* Hero */}
          <div className="mb-12 text-center animate-fade-in-up" data-id="element-956">
            <Badge variant="primary" size="lg" className="mb-6" data-id="element-957">
              Governance
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-100 mb-6" data-id="element-958">
              Governance & Standards
            </h1>
            <p className="text-lg md:text-xl text-slate-400 max-w-3xl mx-auto" data-id="element-959">
              How we maintain quality, accuracy, and alignment with
              international standards. Our commitment to professional-grade
              content.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8" data-id="element-960">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12" data-id="element-961">
              {/* Standards Alignment */}
              <section className="animate-fade-in-up animation-delay-100" data-id="element-962">
                <h2 className="text-2xl font-bold text-slate-100 mb-6 font-mono" data-id="element-963">
                  Standards Alignment
                </h2>
                <p className="text-slate-400 mb-6 leading-relaxed" data-id="element-964">
                  Our protocols are not invented in a vacuum. We strictly align
                  all content with globally recognized cybersecurity frameworks
                  to ensure interoperability and best practice.
                </p>
                <div className="grid gap-4" data-id="element-965">
                  <StandardCard title="NIST Cybersecurity Framework 2.0" desc="Our core organizational structure follows the 6 functions: Govern, Identify, Protect, Detect, Respond, Recover." data-id="element-966" />
                  <StandardCard title="ISO/IEC 27001" desc="Information security management system (ISMS) principles guide our governance and policy templates." data-id="element-967" />
                  <StandardCard title="CIS Controls v8" desc="We use the Critical Security Controls to prioritize actionable defense steps." data-id="element-968" />
                  <StandardCard title="ENISA Guidelines" desc="European Union Agency for Cybersecurity guidelines inform our critical infrastructure content." data-id="element-969" />
                </div>
              </section>

              {/* Content Governance */}
              <section className="animate-fade-in-up animation-delay-200" data-id="element-970">
                <h2 className="text-2xl font-bold text-slate-100 mb-6 font-mono" data-id="element-971">
                  Content Governance
                </h2>
                <Card glass padding="lg" data-id="element-972">
                  <ul className="space-y-4" data-id="element-973">
                    <li className="flex items-start" data-id="element-974">
                      <CheckCircle className="h-5 w-5 text-cyan-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-975" />
                      <div data-id="element-976">
                        <h3 className="font-bold text-slate-200" data-id="element-977">
                          Expert Review
                        </h3>
                        <p className="text-sm text-slate-400" data-id="element-978">
                          All content is reviewed by security professionals
                          before publication.
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start" data-id="element-979">
                      <CheckCircle className="h-5 w-5 text-cyan-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-980" />
                      <div data-id="element-981">
                        <h3 className="font-bold text-slate-200" data-id="element-982">
                          Quarterly Updates
                        </h3>
                        <p className="text-sm text-slate-400" data-id="element-983">
                          We review and update content quarterly to keep pace
                          with the threat landscape.
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start" data-id="element-984">
                      <CheckCircle className="h-5 w-5 text-cyan-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-985" />
                      <div data-id="element-986">
                        <h3 className="font-bold text-slate-200" data-id="element-987">
                          Source Verification
                        </h3>
                        <p className="text-sm text-slate-400" data-id="element-988">
                          All recommendations are backed by reputable sources
                          (NIST, CISA, NCSC).
                        </p>
                      </div>
                    </li>
                  </ul>
                </Card>
              </section>

              {/* Ethical Guidelines */}
              <section className="animate-fade-in-up animation-delay-300" data-id="element-989">
                <h2 className="text-2xl font-bold text-slate-100 mb-6 font-mono" data-id="element-990">
                  Ethical Guidelines
                </h2>
                <div className="grid sm:grid-cols-2 gap-6" data-id="element-991">
                  <Card glass padding="md" data-id="element-992">
                    <h3 className="font-bold text-slate-200 mb-2" data-id="element-993">
                      Defensive Only
                    </h3>
                    <p className="text-sm text-slate-400" data-id="element-994">
                      We strictly prohibit offensive techniques, exploits, or
                      "hacking back" instructions.
                    </p>
                  </Card>
                  <Card glass padding="md" data-id="element-995">
                    <h3 className="font-bold text-slate-200 mb-2" data-id="element-996">
                      Legal Compliance
                    </h3>
                    <p className="text-sm text-slate-400" data-id="element-997">
                      We respect all applicable laws and regulations in the
                      jurisdictions we serve.
                    </p>
                  </Card>
                  <Card glass padding="md" data-id="element-998">
                    <h3 className="font-bold text-slate-200 mb-2" data-id="element-999">
                      Privacy Respect
                    </h3>
                    <p className="text-sm text-slate-400" data-id="element-1000">
                      We do not track users or collect personal data beyond what
                      is necessary for functionality.
                    </p>
                  </Card>
                  <Card glass padding="md" data-id="element-1001">
                    <h3 className="font-bold text-slate-200 mb-2" data-id="element-1002">
                      Accessibility
                    </h3>
                    <p className="text-sm text-slate-400" data-id="element-1003">
                      We strive to make security knowledge available to all,
                      regardless of ability or resources.
                    </p>
                  </Card>
                </div>
              </section>
            </div>

            {/* Sidebar */}
            <div className="space-y-6" data-id="element-1004">
              <Card glass padding="lg" className="sticky top-24 animate-fade-in-up animation-delay-400" data-id="element-1005">
                <h3 className="text-lg font-bold text-slate-100 mb-4 font-mono" data-id="element-1006">
                  Quick Links
                </h3>
                <div className="space-y-2" data-id="element-1007">
                  <Link to="/about/mission" className="block p-2 rounded hover:bg-slate-800/50 text-slate-400 hover:text-cyan-400 transition-colors" data-id="element-1008">
                    Mission & Vision
                  </Link>
                  <Link to="/about/scope" className="block p-2 rounded hover:bg-slate-800/50 text-slate-400 hover:text-cyan-400 transition-colors" data-id="element-1009">
                    Scope & Non-Guarantee
                  </Link>
                  <Link to="/about/standards" className="block p-2 rounded hover:bg-slate-800/50 text-slate-400 hover:text-cyan-400 transition-colors" data-id="element-1010">
                    Standards Mapping
                  </Link>
                  <Link to="/about/changelog" className="block p-2 rounded hover:bg-slate-800/50 text-slate-400 hover:text-cyan-400 transition-colors" data-id="element-1011">
                    Changelog
                  </Link>
                  <Link to="/about/governance" className="block p-2 rounded bg-cyan-500/10 text-cyan-400 font-medium border border-cyan-500/20" data-id="element-1012">
                    Governance
                  </Link>
                  <Link to="/about/disclaimer" className="block p-2 rounded hover:bg-slate-800/50 text-slate-400 hover:text-cyan-400 transition-colors" data-id="element-1013">
                    Legal Disclaimer
                  </Link>
                  <Link to="/about/contact" className="block p-2 rounded hover:bg-slate-800/50 text-slate-400 hover:text-cyan-400 transition-colors" data-id="element-1014">
                    Contact Us
                  </Link>
                </div>

                <div className="mt-8 pt-6 border-t border-slate-800" data-id="element-1015">
                  <h4 className="text-sm font-bold text-slate-400 uppercase mb-4 font-mono" data-id="element-1016">
                    Start Learning
                  </h4>
                  <Link to="/start" className="block w-full" data-id="element-1017">
                    <Button variant="primary" fullWidth data-id="element-1018">
                      Get Started <ArrowRight className="ml-2 h-4 w-4" data-id="element-1019" />
                    </Button>
                  </Link>
                </div>
              </Card>
            </div>
          </div>
        </Container>
      </Section>
    </Layout>;
}
function StandardCard({
  title,
  desc
}: {
  title: string;
  desc: string;
}) {
  return <Card glass padding="md" className="flex items-start" data-id="element-1020">
      <Shield className="h-6 w-6 text-cyan-400 mr-4 flex-shrink-0" data-id="element-1021" />
      <div data-id="element-1022">
        <h3 className="font-bold text-slate-200" data-id="element-1023">{title}</h3>
        <p className="text-sm text-slate-400" data-id="element-1024">{desc}</p>
      </div>
    </Card>;
}